import React, { useState } from 'react';
import { Footer, GradientHero, SEO, breadcrumbSchema } from '@coupling/ui';
import Header from '../components/Header';
import {
  merchantLaunchOperatorCampaigns,
  merchantLaunchOperatorRows,
  merchantLaunchOperatorTsv,
  type MerchantLaunchOperatorRow,
} from '../content/facebookMerchantLaunchBundle';
import { seoConfig } from '../config/seo';

const copyToClipboard = async (value: string) => {
  if (
    typeof navigator === 'undefined' ||
    !navigator.clipboard?.writeText
  ) {
    throw new Error(
      'Clipboard API is unavailable in this browser context (HTTPS required).',
    );
  }
  await navigator.clipboard.writeText(value);
};

type CopyButtonProps = {
  copiedKey: string | null;
  copyId: string;
  label: string;
  value: string;
  onCopy: (copyId: string, value: string) => Promise<void>;
};

const CopyButton: React.FC<CopyButtonProps> = ({
  copiedKey,
  copyId,
  label,
  value,
  onCopy,
}) => (
  <button
    type="button"
    onClick={() => void onCopy(copyId, value)}
    className="rounded-full border border-[#d8bfaa] bg-white px-4 py-2 text-sm font-semibold text-[#7b4a31] transition hover:border-[#c8875b] hover:text-[#5f311e]"
  >
    {copiedKey === copyId ? 'Copied' : label}
  </button>
);

type OperatorFieldProps = {
  copiedKey: string | null;
  copyId: string;
  label: string;
  value: string;
  onCopy: (copyId: string, value: string) => Promise<void>;
};

const OperatorField: React.FC<OperatorFieldProps> = ({
  copiedKey,
  copyId,
  label,
  value,
  onCopy,
}) => (
  <div className="rounded-[1.25rem] border border-[#ead9ca] bg-white p-4">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-brand-primary">
          {label}
        </p>
        <p className="mt-2 break-words text-sm leading-relaxed text-neutral-700">
          {value}
        </p>
      </div>
      <CopyButton
        copiedKey={copiedKey}
        copyId={copyId}
        label="Copy"
        value={value}
        onCopy={onCopy}
      />
    </div>
  </div>
);

type LaunchRowCardProps = {
  copiedKey: string | null;
  row: MerchantLaunchOperatorRow;
  onCopy: (copyId: string, value: string) => Promise<void>;
};

const LaunchRowCard: React.FC<LaunchRowCardProps> = ({
  copiedKey,
  row,
  onCopy,
}) => (
  <article className="rounded-[2rem] border border-[#e7d8c9] bg-white p-6 shadow-[0_26px_70px_rgba(77,44,25,0.10)] sm:p-8">
    <div className="flex flex-wrap items-center gap-3">
      <span className="rounded-full bg-[#f6e6d6] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#8e4a28]">
        {row.angle}
      </span>
      <span className="rounded-full bg-[#f8f2eb] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-neutral-600">
        {row.formatLabel}
      </span>
      <span className="rounded-full bg-[#eff5ef] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#356251]">
        {row.metaCta}
      </span>
    </div>

    <h2 className="mt-4 text-2xl font-semibold tracking-tight text-neutral-900">
      {row.adName}
    </h2>

    <div className="mt-6 grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
      <div className="rounded-[1.6rem] border border-[#ead9ca] bg-[#fffaf5] p-4">
        <img
          src={row.assetPublicUrl}
          alt={`${row.adName} export`}
          className="w-full rounded-[1.2rem] border border-[#e6d6c7] bg-[#f7ede2] object-cover"
        />
        <div className="mt-4 flex flex-wrap gap-3">
          <a
            href={row.assetPublicUrl}
            target="_blank"
            rel="noreferrer"
            className="rounded-full bg-[#8e4a28] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#6f371c]"
          >
            Open asset
          </a>
          <a
            href={row.destinationUrl}
            target="_blank"
            rel="noreferrer"
            className="rounded-full border border-[#d7bfa9] bg-white px-4 py-2 text-sm font-semibold text-[#7b4a31] transition hover:border-[#c8875b]"
          >
            Open landing page
          </a>
        </div>
      </div>

      <div className="grid gap-4">
        <div className="grid gap-4 lg:grid-cols-2">
          <OperatorField
            copiedKey={copiedKey}
            copyId={`${row.formatId}-campaign`}
            label="Campaign"
            value={row.campaignName}
            onCopy={onCopy}
          />
          <OperatorField
            copiedKey={copiedKey}
            copyId={`${row.formatId}-ad-set`}
            label="Ad Set"
            value={row.adSetName}
            onCopy={onCopy}
          />
          <OperatorField
            copiedKey={copiedKey}
            copyId={`${row.formatId}-ad-name`}
            label="Ad Name"
            value={row.adName}
            onCopy={onCopy}
          />
          <OperatorField
            copiedKey={copiedKey}
            copyId={`${row.formatId}-placements`}
            label="Placements"
            value={row.placements}
            onCopy={onCopy}
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <OperatorField
            copiedKey={copiedKey}
            copyId={`${row.formatId}-asset-path`}
            label="Asset Workspace Path"
            value={row.assetWorkspacePath}
            onCopy={onCopy}
          />
          <OperatorField
            copiedKey={copiedKey}
            copyId={`${row.formatId}-destination`}
            label="Destination URL"
            value={row.destinationUrl}
            onCopy={onCopy}
          />
        </div>

        <OperatorField
          copiedKey={copiedKey}
          copyId={`${row.formatId}-primary`}
          label="Primary Text"
          value={row.primaryText}
          onCopy={onCopy}
        />

        <div className="grid gap-4 lg:grid-cols-2">
          <OperatorField
            copiedKey={copiedKey}
            copyId={`${row.formatId}-headline`}
            label="Headline"
            value={row.headline}
            onCopy={onCopy}
          />
          <OperatorField
            copiedKey={copiedKey}
            copyId={`${row.formatId}-description`}
            label="Description"
            value={row.description}
            onCopy={onCopy}
          />
        </div>
      </div>
    </div>
  </article>
);

type FacebookMerchantLaunchConsoleProps = {
  copyValue?: (value: string) => Promise<void>;
};

export const FacebookMerchantLaunchConsole: React.FC<
  FacebookMerchantLaunchConsoleProps
> = ({ copyValue = copyToClipboard }) => {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);

  const handleCopy = async (copyId: string, value: string) => {
    try {
      await copyValue(value);
      setCopyError(null);
      setCopiedKey(copyId);
      window.setTimeout(() => {
        setCopiedKey((current) => (current === copyId ? null : current));
      }, 1600);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Copy failed. Try again.';
      setCopyError(message);
      setCopiedKey(null);
      window.setTimeout(() => {
        setCopyError((current) => (current === message ? null : current));
      }, 4000);
    }
  };

  return (
    <div className="min-h-screen bg-[#fff8f1]">
      <SEO
        title={`Meta Launch Console - ${seoConfig.siteName}`}
        description="Operator-facing Meta Ads Manager launch console for the Coupling WNC merchant-acquisition campaign."
        canonical={`${seoConfig.siteUrl}/preview/facebook-merchant-launch-console`}
        ogImage={seoConfig.defaultOgImage}
        ogUrl={`${seoConfig.siteUrl}/preview/facebook-merchant-launch-console`}
        jsonLd={breadcrumbSchema([
          { name: 'Home', url: seoConfig.siteUrl },
          {
            name: 'Meta Launch Console',
            url: `${seoConfig.siteUrl}/preview/facebook-merchant-launch-console`,
          },
        ])}
      />
      <Header />

      <GradientHero
        title="Meta Launch Console"
        description="Operator view for moving the WNC merchant-acquisition package into Ads Manager without hunting for names, files, or URLs."
        className="py-16"
      >
        <div className="mx-auto mt-8 max-w-5xl rounded-[1.85rem] border border-white/20 bg-white/10 px-6 py-5 text-left text-white/92 backdrop-blur-sm">
          <div className="grid gap-4 lg:grid-cols-[1.4fr_0.6fr]">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.16em] text-white/72">
                Launch Flow
              </p>
              <p className="mt-3 text-base leading-relaxed">
                Build the {merchantLaunchOperatorCampaigns.length} campaigns, create the{' '}
                {merchantLaunchOperatorCampaigns.reduce(
                  (count, campaign) => count + campaign.adSets.length,
                  0,
                )}{' '}
                ad sets, then work through the {merchantLaunchOperatorRows.length} ad rows
                below. Each row already contains the asset, ad name, copy, Meta CTA, and
                tracked destination URL.
              </p>
            </div>
            <div className="flex flex-wrap items-start gap-3 lg:justify-end">
              <CopyButton
                copiedKey={copiedKey}
                copyId="full-launch-sheet"
                label="Copy full launch sheet"
                value={merchantLaunchOperatorTsv}
                onCopy={handleCopy}
              />
              <a
                href="/preview/facebook-merchant-launch-package"
                className="rounded-full border border-white/35 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/10"
              >
                Open launch bundle
              </a>
            </div>
          </div>
          {copyError && (
            <p
              role="alert"
              className="mt-3 text-sm font-medium text-[#ffd0bf]"
            >
              {copyError}
            </p>
          )}
        </div>
      </GradientHero>

      <section className="px-4 py-16">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-[#e7d8c9] bg-white p-6 shadow-[0_26px_70px_rgba(77,44,25,0.10)] sm:p-8">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-brand-primary">
              Build order
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
              Set the campaign shell once, then move row by row.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-neutral-600">
              This page is intentionally operational. The campaign definitions live at the top, and the ad rows below
              are optimized for copy-paste into Ads Manager.
            </p>
          </div>

          <div className="mt-8 grid gap-4 xl:grid-cols-2">
            {merchantLaunchOperatorCampaigns.map((campaign) => (
              <article
                key={campaign.campaignName}
                className="rounded-[1.6rem] border border-[#ead9ca] bg-[#fffaf5] p-5 shadow-sm"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <span className="rounded-full bg-[#f6e6d6] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#8e4a28]">
                    {campaign.objective}
                  </span>
                  <span className="rounded-full bg-[#eef5ee] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#356251]">
                    {campaign.conversionLocation}
                  </span>
                </div>
                <h3 className="mt-4 text-2xl font-semibold text-neutral-900">
                  {campaign.campaignName}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-neutral-600">
                  {campaign.useCase}
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <CopyButton
                    copiedKey={copiedKey}
                    copyId={campaign.campaignName}
                    label="Copy campaign name"
                    value={campaign.campaignName}
                    onCopy={handleCopy}
                  />
                  <span className="rounded-full bg-white px-3 py-2 text-sm font-medium text-neutral-700">
                    {campaign.dailyBudget}
                  </span>
                  <span className="rounded-full bg-white px-3 py-2 text-sm font-medium text-neutral-700">
                    {campaign.performanceGoal}
                  </span>
                </div>

                <div className="mt-6 grid gap-3">
                  {campaign.adSets.map((adSet) => (
                    <div
                      key={adSet.adSetName}
                      className="rounded-[1.1rem] border border-[#ead9ca] bg-white p-4"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-neutral-900">
                            {adSet.adSetName}
                          </p>
                          <p className="mt-1 text-sm text-neutral-600">
                            {adSet.audience}
                          </p>
                        </div>
                        <CopyButton
                          copiedKey={copiedKey}
                          copyId={adSet.adSetName}
                          label="Copy ad set"
                          value={adSet.adSetName}
                          onCopy={handleCopy}
                        />
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="rounded-full bg-[#f8f2eb] px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-neutral-600">
                          {adSet.budget}
                        </span>
                        <span className="rounded-full bg-[#eef5ee] px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-[#356251]">
                          {adSet.metaCta}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 pb-20">
        <div className="mx-auto flex max-w-7xl flex-col gap-10">
          {merchantLaunchOperatorRows.map((row) => (
            <LaunchRowCard
              key={row.formatId}
              copiedKey={copiedKey}
              row={row}
              onCopy={handleCopy}
            />
          ))}
        </div>
      </section>

      <Footer description="Operator-facing Meta Ads Manager launch console for the Coupling WNC merchant-acquisition campaign." />
    </div>
  );
};

export default FacebookMerchantLaunchConsole;
